import {
  WSCard,
  WSEmptyState,
  WSFlexBox
} from "@wingspanhq/fe-component-library";
import {
  PayoutDestinationType,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { queriesInternalAccounts } from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { selectorPayoutSettingsDefaultDestination } from "@selectors/selectorPayoutSettingsDefaultDestination";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { BadgePayoutPreference } from "../BadgePayoutPreference";
import {
  PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD,
  PATH_SETTINGS_PAYMENT_METHODS_MANAGE_PAYOUT_METHOD
} from "../../../Settings/paths";
import { LabelMethod } from "../LabelMethod";
import { MethodType } from "../ManageMethods/types";

type Props = { noDescription?: boolean };

export const DefaultPayoutMethod: React.FC<Props> = ({ noDescription }) => {
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const history = useHistory();
  const defaultPayoutMethod = useDefaultPayoutMethod();

  return (
    <WSCard
      shimmer={queryPayoutSettings.isLoading}
      divider={!noDescription}
      header={{
        label: {
          text: "Payout method",
          helperText: noDescription
            ? undefined
            : "Default method to receive payments from clients"
        },
        value: noDescription
          ? undefined
          : {
              pill: {
                theme: "neutral",
                text: "Default",
                badge: true
              }
            }
      }}
      actions={{
        alignment: "left",
        buttons: [
          {
            kind: "Link",
            label: "Manage payout method",
            rightIcon: "chevron-right",
            onClick: () => {
              history.push(PATH_SETTINGS_PAYMENT_METHODS_MANAGE_PAYOUT_METHOD);
            }
          }
        ]
      }}
    >
      {!!defaultPayoutMethod ? (
        <WSFlexBox justify="space-between" alignItems="center" wrap="nowrap">
          <LabelMethod
            type={defaultPayoutMethod.type}
            id={defaultPayoutMethod.id}
          />
          {queryPayoutSettings.data?.preferredPayoutSpeed && (
            <BadgePayoutPreference
              payoutPreference={queryPayoutSettings.data.preferredPayoutSpeed}
            />
          )}
        </WSFlexBox>
      ) : (
        <WSEmptyState
          type="card"
          title="No payout method added"
          orientation="horizontal"
          description="Add a default payout method to get paid."
          size="S"
          actions={{
            buttons: [
              {
                label: "Add payout method",
                kind: "Secondary",
                onClick: () => {
                  history.push(PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD);
                }
              }
            ]
          }}
        />
      )}
    </WSCard>
  );
};

export function payoutDestinationTypeToMethodType(
  destinationType?: PayoutDestinationType
): MethodType {
  switch (destinationType) {
    case PayoutDestinationType.EXTERNAL_BANK_ACCOUNT:
      return "Account";
    case PayoutDestinationType.PAYMENT_CARD:
      return "PaymentCard";
    case PayoutDestinationType.INTERNAL_ACCOUNT:
      return "InternalAccount";
    case PayoutDestinationType.GIFT_CARD:
      return "GiftCard";
    default:
      return "Historical";
  }
}

export const useDefaultPayoutMethod = () => {
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();

  return useMemo(():
    | {
        type: MethodType;
        id: string;
        category: PreferredPayoutSpeed;
      }
    | undefined => {
    const payoutSettings = queryPayoutSettings.data;

    if (!payoutSettings) {
      return;
    }

    const category =
      payoutSettings.preferredPayoutSpeed || PreferredPayoutSpeed.STANDARD;
    const destination =
      selectorPayoutSettingsDefaultDestination(payoutSettings);

    // If no payout destination is set, check if there is Wallet
    if (!destination) {
      const bankingInternalAccount = queryInternalAccounts.data?.data?.find(
        selectorActiveBankingAccount
      );

      if (!bankingInternalAccount) {
        return;
      }

      return {
        type: "InternalAccount",
        id: bankingInternalAccount.id,
        category
      };
    }

    return {
      type: payoutDestinationTypeToMethodType(destination.type),
      id: destination.id ?? "",
      category
    };
  }, [queryInternalAccounts.data, queryPayoutSettings.data]);
};
